import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { m } from 'motion/react';
import { CADENCIA, resumoQuery } from '../lib/chat-transport';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notificacoesQuery } from '../api/notificacoes';
import { api, euQuery, type Usuario } from '../api/cliente';
import { voltarAoPainel } from '../api/admin';
import { MOLA_TROCA } from '../lib/movimento';
import { AnunciadorDeRota } from './AnunciadorDeRota';
import { CodeCoffeeLogo } from './CodeCoffeeLogo';
import { ConviteInstalar } from './ConviteInstalar';
import { Icone, type NomeIcone } from './Icone';
import { Logo } from './Logo';
import { PularParaConteudo } from './PularParaConteudo';
import { ModalReaceite } from './ModalReaceite';
import { PopupNovidades } from './PopupNovidades';
import { BotaoAnaBarra, BotaoSuporte, SuporteGaveta } from './suporte/BotaoSuporte';
import { AjustesDeLeitura } from './ui/AjustesDeLeitura';
import { MenuConta } from './ui/MenuConta';

export function Layout() {
  const { data: usuario } = useQuery(euQuery);
  return <>{<PularParaConteudo />}<AnunciadorDeRota />{usuario ? <><AreaAutenticada usuario={usuario} />{usuario.aceite_pendente ? <ModalReaceite usuario={usuario} /> : <PopupNovidades />}</> : <AreaPublica />}</>;
}

function AreaPublica() {
  return (
    <div className="min-h-dvh">
      <header className="cabecalho-site">
        <div className="cabecalho-site-marca">
          <Link to="/" className="marca-site" aria-label="Chama Um, início"><Logo tamanho={31} /></Link>
          <nav aria-label="Principal" className="cabecalho-acoes">
            <ul className="cabecalho-lista">
              <li><Link to="/buscar" search={{}} className="acao-topo" aria-label="Buscar profissional"><Icone nome="buscar" /><span>Buscar</span></Link></li>
              <li className="so-largo"><Link to="/ajuda" className="acao-topo"><Icone nome="info" /><span>Ajuda</span></Link></li>
              <li><Link to="/entrar" className="acao-topo"><Icone nome="conta" /><span>Entrar</span></Link></li>
              <li><AjustesDeLeitura /></li>
              <li className="so-largo"><Link to="/cadastrar" className="acao-primaria">Criar conta</Link></li>
            </ul>
          </nav>
        </div>
      </header>
      <main id="conteudo" className="conteudo-publico"><Outlet /></main>
      <Rodape />
    </div>
  );
}

function AreaAutenticada({ usuario }: { usuario: Usuario }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const podeTrabalhar = usuario.perfil_status === 'verificado';
  const profissionalAtivo = podeTrabalhar && pathname.startsWith('/pro/');
  const contexto = profissionalAtivo ? 'profissional' : 'cliente';
  const links = linksDoContexto(contexto);
  const barra = links.filter((l) => l.to !== '/app/conta' && (contexto === 'cliente' || l.to !== '/app/chamados')).slice(0, 4);
  const qc = useQueryClient();
  const sair = useMutation({
    mutationFn: () => api.post<{ ok: true }>('/auth/sair'),
    // Recarrega a pagina inteira: `qc.clear()` remove as queries do cache mas
    // nao notifica os observers ja montados, entao a tela continuaria mostrando
    // o usuario antigo ate um F5. Sair tambem precisa descartar todo o estado
    // em memoria da sessao anterior.
    onSettled: () => { qc.clear(); window.location.assign('/'); },
  });

  return (
    <div className="app-shell">
      {usuario.acesso_admin && <FaixaAcessoAdmin nome={usuario.nome} />}
      <header className="cabecalho-site">
        <div className="cabecalho-site-marca">
          <Link to="/" className="marca-site" aria-label="Chama Um, início"><Logo tamanho={29} /></Link>
          {podeTrabalhar && (
            <m.nav layoutRoot aria-label="Contexto de uso" className="nav-contexto so-largo">
              <LinkDeContexto to="/" ativo={contexto === 'cliente'}>Contratar</LinkDeContexto>
              <LinkDeContexto to="/pro/inicio" ativo={contexto === 'profissional'} rotulo="Trabalhar: início">Trabalhar</LinkDeContexto>
            </m.nav>
          )}
          <div className="cabecalho-acoes">
            <LinkDeConversas />
            <LinkDeAvisos />
            <BotaoSuporte className="so-largo" />
            <AjustesDeLeitura />
            <MenuConta
              nome={primeiroNome(usuario.nome)}
              saindo={sair.isPending}
              aoSair={() => sair.mutate()}
              cadastroPro={podeTrabalhar ? undefined : { rotulo: usuario.eh_profissional ? 'Meu cadastro profissional' : 'Quero trabalhar' }}
              painel={usuario.eh_admin && !usuario.acesso_admin}
              trocaDeContexto={podeTrabalhar ? { para: contexto === 'cliente' ? 'profissional' : 'cliente' } : undefined}
            />
          </div>
        </div>
      </header>
      <div className="app-shell-corpo">
        <aside className="app-sidebar" aria-label="Navegação do aplicativo">
          {/* layoutRoot: a barra é sticky e a troca de tela rola a página ao topo;
              medida relativa a ela, a luz não pula. */}
          <m.nav layoutRoot className="nav-app" aria-label={contexto === 'cliente' ? 'Área de contratação' : 'Área profissional'}>
            {links.map((link) => <LinkNavegacao key={link.to} {...link} luz="lateral" />)}
            {contexto === 'cliente' && <Link to="/app/pedir" className="acao-primaria nav-app-acao"><Icone nome="adicionar" />Pedir serviço</Link>}
            {!podeTrabalhar && <Link to="/pro/cadastro" className="acao-secundaria nav-app-acao">{usuario.eh_profissional ? 'Meu cadastro profissional' : 'Quero trabalhar'}</Link>}
          </m.nav>
        </aside>
        <main id="conteudo" className="app-shell-conteudo conteudo-app">
          {/* Só na tela de início: convite repetido em toda tela vira incômodo. */}
          {(pathname === '/' || pathname === '/pro/inicio') && <ConviteInstalar />}
          <Outlet />
        </main>
      </div>
      <m.nav layoutRoot className="nav-mobile" aria-label={contexto === 'cliente' ? 'Navegação de contratação' : 'Navegação profissional'}>
        {/* Quatro destinos e a Ana no centro. Conta já está no topo (menu da
            pessoa); no profissional, Pedidos fica no início e no menu. */}
        {barra.slice(0, 2).map((link) => <LinkNavegacao key={link.to} {...link} luz="barra" />)}
        <BotaoAnaBarra />
        {barra.slice(2).map((link) => <LinkNavegacao key={link.to} {...link} luz="barra" />)}
      </m.nav>
      <SuporteGaveta area={contexto} />
    </div>
  );
}

/**
 * O dono do painel está dentro da conta de outra pessoa ("acessar como").
 * Fica no topo de toda tela para ninguém esquecer de quem é a conta.
 * Se a sessão do painel já caiu, sai da conta e manda entrar no painel de novo.
 */
function FaixaAcessoAdmin({ nome }: { nome: string }) {
  const voltar = useMutation({
    mutationFn: async () => {
      try { await voltarAoPainel(); return '/admin/acesso'; } catch {
        await api.post('/auth/sair').catch(() => undefined);
        return '/admin/entrar';
      }
    },
    onSuccess: (destino) => window.location.assign(destino),
  });
  return (
    <div className="faixa-acesso-admin" role="region" aria-label="Acesso pelo painel">
      <p>Você está vendo o app como <strong>{nome}</strong>, pelo painel da equipe.</p>
      <button type="button" className="acao-link" onClick={() => voltar.mutate()} disabled={voltar.isPending}>
        <Icone nome="sair" />{voltar.isPending ? 'Voltando…' : 'Voltar ao painel'}
      </button>
    </div>
  );
}

/** Contratar/Trabalhar: a luz do lado ativo desliza para o outro lado. */
function LinkDeContexto({ to, ativo, rotulo, children }: { to: '/' | '/pro/inicio'; ativo: boolean; rotulo?: string; children: string }) {
  return (
    <Link to={to} aria-label={rotulo} aria-current={ativo ? 'page' : undefined}>
      {ativo && <m.span layoutId="luz-contexto" className="nav-contexto-luz" aria-hidden="true" transition={MOLA_TROCA} />}
      {children}
    </Link>
  );
}

function Rodape() {
  return (
    <footer className="rodape-site">
      <div className="rodape-site-conteudo">
        <div className="rodape-site-topo">
          <Link to="/" className="marca-site" aria-label="Chama Um, início"><Logo tamanho={30} /></Link>
          <nav aria-label="Rodapé">
            <ul className="rodape-site-links">
              <li><Link to="/buscar" search={{}} className="acao-link">Buscar profissional</Link></li>
              <li><Link to="/acessibilidade" className="acao-link">Acessibilidade</Link></li>
              <li><Link to="/novidades" className="acao-link">Novidades</Link></li>
              <li><Link to="/ajuda" className="acao-link">Ajuda</Link></li>
              <li><a href="https://www.instagram.com/chamaumoficial/" className="acao-link" target="_blank" rel="noopener noreferrer" aria-label="Instagram do Chama Um — abre em nova aba"><Icone nome="instagram" /> Instagram</a></li>
            </ul>
          </nav>
        </div>
        <p className="rodape-site-frase">Chama Um conecta você a profissionais com identidade conferida e pagamento protegido.</p>
        <div className="rodape-barra">
          <p>© {new Date().getFullYear()} Chama Um</p>
          <a className="credito-codecoffee" href="https://codecoffee.com.br/" target="_blank" rel="noopener noreferrer" aria-label="Desenvolvido por CodeCoffee — abre em nova aba">
            <span>Desenvolvido por</span>
            <CodeCoffeeLogo />
            <strong>Code<span>Coffee</span></strong>
          </a>
        </div>
      </div>
    </footer>
  );
}

type LinkDoApp = { to: string; texto: string; icone: NomeIcone };
function linksDoContexto(contexto: 'cliente' | 'profissional'): LinkDoApp[] {
  return contexto === 'profissional'
    ? [{ to: '/pro/inicio', texto: 'Início', icone: 'inicio' }, { to: '/pro/oportunidades', texto: 'Oportunidades', icone: 'trabalho' }, { to: '/pro/agenda', texto: 'Agenda', icone: 'agenda' }, { to: '/app/chamados', texto: 'Pedidos', icone: 'pedidos' }, { to: '/pro/gestao', texto: 'Meu negócio', icone: 'negocio' }]
    : [{ to: '/', texto: 'Início', icone: 'inicio' }, { to: '/buscar', texto: 'Buscar', icone: 'buscar' }, { to: '/app/chamados', texto: 'Pedidos', icone: 'pedidos' }, { to: '/app/casa', texto: 'Minha casa', icone: 'favorito' }, { to: '/app/conta', texto: 'Conta', icone: 'conta' }];
}

/**
 * Item da navegação. A luz cobalto do item atual é um elemento só, que
 * desliza de um item para o outro (layoutId); cada navegação tem a sua.
 */
function LinkNavegacao({ to, texto, icone, luz }: LinkDoApp & { luz: 'lateral' | 'barra' }) {
  const conteudo = ({ isActive }: { isActive: boolean }) => (
    <>
      {isActive && <m.span layoutId={`luz-${luz}`} className="nav-app-luz" aria-hidden="true" transition={MOLA_TROCA} />}
      <Icone nome={icone} />
      <span>{texto}</span>
    </>
  );
  const exato = { exact: to === '/' || to === '/pro/inicio' };
  if (to === '/buscar') {
    return <Link to="/buscar" search={{}} aria-label="Buscar profissional" className="nav-app-link" activeOptions={{ ...exato, includeSearch: false }}>{conteudo}</Link>;
  }
  return <Link to={to as never} className="nav-app-link" activeOptions={exato}>{conteudo}</Link>;
}

function primeiroNome(nome: string): string { return nome.trim().split(/\s+/)[0] ?? nome; }

/** Conversas com mensagem não lida. Consulta a rota barata, a cada 30s. */
function LinkDeConversas() {
  const { data } = useQuery({ ...resumoQuery, refetchInterval: CADENCIA.lista });
  const n = (data?.conversas ?? []).reduce((s, c) => s + c.nao_lidas, 0);
  return <Link to="/app/conversas" className="acao-topo" aria-label={n === 0 ? 'Conversas' : `Conversas, ${n} ${n === 1 ? 'mensagem não lida' : 'mensagens não lidas'}`}><Icone nome="conversas" /><span>Conversas</span>{n > 0 && <span className="contador-avisos" aria-hidden="true">{n}</span>}</Link>;
}

function LinkDeAvisos() {
  const { data } = useQuery(notificacoesQuery);
  const n = data?.nao_lidas ?? 0;
  return <Link to="/app/avisos" className="acao-topo" aria-label={n === 0 ? 'Avisos' : `Avisos, ${n} ${n === 1 ? 'não lido' : 'não lidos'}`}><Icone nome="avisos" /><span>Avisos</span>{n > 0 && <span className="contador-avisos" aria-hidden="true">{n}</span>}</Link>;
}
